import React from 'react';
import { UserPlus, UserMinus, RefreshCw, Stethoscope, CalendarX, Percent, CalendarClock, Cake, DoorOpen, CalendarCheck, ArrowUp, ArrowDown, ChevronsUpDown, Gauge, TrendingUp, Users } from 'lucide-react';
import { COR, FONTE_DADOS, cartao, fmtNum, fmt1, LinhaMercado, CARD_PAD, CARD_MIN_H, CARD_GAP, rotuloCard, valorCard, TituloBloco } from './ui';

// =====================================================================
// VISÃO RH do Dashboard de Operação (Fase 1).
// =====================================================================
// Consome /api/home/rh/painel (via DashboardOperacao, que segura o filtro
// Ano/Mês/loja e o toggle). Blocos:
//   - Faixa de indicadores em cards (Contratados/Desligados/Turnover +          [topo]
//     Faltas / Atestados (contagem) / % Absenteísmo — chip de desvio x mercado),
//     no MESMO estilo dos cards de venda da aba Operação
//   - Real × Ideal por cargo (tabela simples, uma linha por cargo) [meio, esquerda]
//   - Experiência (em cima) + Aniversariantes (embaixo)            [meio, direita]
//   - Nota Desligamento · Nota 15 dias (placeholder — Fase 2)       [faixa inferior]
// Estilos inline (convenção do projeto), paleta em ./ui.js.
//
// FONTE: esta tela é montada DENTRO do DashboardOperacao, e herda dele a família
// dos dados (`raizDashboard` do ui.js) — por isso aqui não se pede fontFamily em
// lugar nenhum, exceto nos <button> (que não herdam fonte por padrão).

// fmtNum centralizado em ./ui.js (headcounts do RH ficam cheios — não abreviamos).
// 'YYYY-MM-DD' → 'DD/MM/YYYY'
const fmtData = (iso) => (!iso ? '—' : `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`);
// GC/hora com 2 casas: 7.96 → "7,96". A escala é curta (3 a 8), então 1 casa só
// esconderia diferença real entre lojas vizinhas do ranking.
const fmtGcH = (n) => (n == null ? '—' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Régua de altura das três tabelas do meio (Real × Ideal · Experiência ·
// Aniversariantes). ⚠️ É ALTURA ÚNICA, e é isso — não o `align-items` — que
// mantém as três terminando na MESMA linha: cada lista tem um tamanho que muda
// com o recorte (12 cargos, 30 pessoas em experiência, 40 aniversariantes…) e,
// sem uma régua, cada card para numa altura diferente.
//
// ⚠️⚠️ 460 → 500px em 11/09/2026, e a altura É a resposta ao pedido da Julia
//    ("tirar a barra de rolagem do Quadro Real × Ideal"). O quadro dela pede
//    ~466px e não cabia nos 460 por uma diferença de linha — daí a barra. Com
//    500 ele cabe inteiro e a barra não aparece.
//    ⚠️ Duas tentativas ANTES desta falharam, e as duas por mexer no alinhamento
//    em vez de na altura: deixar a linha crescer (`minmax`) esticou Experiência e
//    Aniversariantes junto, que perderam a rolagem delas; pôr `maxHeight` só
//    nesses dois travou-os em 460 enquanto o Real × Ideal ia a 466, e os cards
//    passaram a terminar 6px fora de registro. Mexer aqui é mexer nos TRÊS.
//
// ⚠️ Quanto cabe: **medido em 11/09**, uma loja tem 10–11 cargos e a rede 12 —
//    ambos cabem. A rede COM o escritório chega a 42, e aí a barra volta, porque
//    a alternativa seria o card vazar por cima da seção de baixo.
const ALTURA_TABELA = '500px';

// 'YYYY-MM-DD' de hoje no fuso local. O toISOString().slice(0,10) daria a data em
// UTC — de noite, no Brasil, já viraria o dia seguinte e venceria prazo cedo demais.
function hojeISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// --- Bloco 1: Real × Ideal por cargo -----------------------------------------
// Tabela simples: uma linha por CARGO (na ordem que o backend manda) + Total no
// rodapé. dif = real − ideal. Cor (pedido da Julia, 2026-07-15): excedente
// (real>ideal) = verde; abaixo do ideal = vermelho; no ponto exato (=ideal) =
// amarelo. Mais gente que o ideal é lido como positivo, não como custo.
// Colunas na ordem da leitura do quadro (Julia, 2026-07-16): Real → Ideal → Dif.
// → Fant. — o Fant. fecha a linha porque é informativo, não entra na conta.
// Cargos do grupo ESCRITORIO não vêm (filtrados no backend).
function BlocoRealIdeal({ quadro }) {
    // Colunas numéricas CENTRALIZADAS, rótulo e número (Julia, 2026-07-16) — os
    // headcounts aqui são curtos (1 a 3 dígitos), então centralizar lê melhor do que
    // encostar tudo na direita. Só a coluna Cargo (texto) fica à esquerda.
    // O cabeçalho é o `thMini` das outras duas tabelas da linha (Experiência e
    // Aniversariantes): esta tabela tinha faixa vermelha e card de outro padding, e
    // as três, lado a lado, pareciam de telas diferentes (Julia, 2026-09-11).
    // Padding horizontal enxuto (6px) para as 5 colunas caberem sem rolagem.
    const td = { padding: '7px 6px', fontSize: '13px', textAlign: 'center', color: COR.texto, whiteSpace: 'nowrap' };
    // Cargo encosta na borda esquerda do card, como o nome do colaborador nas outras.
    const tdCargo = { ...td, textAlign: 'left', padding: '7px 6px 7px 0', fontWeight: 600, whiteSpace: 'normal' };
    const corDif = (d) => (d > 0 ? '#16a34a' : d < 0 ? '#dc2626' : '#ca8a04');
    const chipDif = (d) => (
        <span style={{ fontWeight: 700, color: corDif(d) }}>{d > 0 ? `+${d}` : d}</span>
    );
    // Fantasmas em PRETO como os demais (Julia, 2026-07-16): o vermelho de alerta
    // competia com o vermelho da Dif. negativa, que é o que a tabela quer destacar.
    const celFant = (n) => <td style={td}>{fmtNum(n || 0)}</td>;

    const totReal = (quadro || []).reduce((s, l) => s + l.real, 0);
    const totIdeal = (quadro || []).reduce((s, l) => s + l.ideal, 0);
    const totFant = (quadro || []).reduce((s, l) => s + (l.fantasmas || 0), 0);

    // Casca IDÊNTICA à da Experiência e à dos Aniversariantes (mesma linha do
    // dashboard): mesmo padding de card, título direto no topo e área rolável com
    // cabeçalho fixo. `minHeight: 0` é o que permite a área ENCOLHER dentro do flex e
    // rolar — sem ele o padrão (`min-height: auto`) deixa o conteúdo empurrar o card.
    return (
        <div style={{ ...cartao, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%', minHeight: 0 }}>
            <TituloBloco icone={<Users size={18} />} cor={COR.vermelhoEscuro} texto="Quadro Real × Ideal por cargo" />
            {/* ⚠️ A barra de rolagem daqui saiu por ALTURA, não por `overflow` (Julia,
                11/09/2026): o `auto` continua, e é ele que impede o card de vazar por
                cima da seção de baixo no recorte com escritório (42 cargos). O que
                tirou a barra do dia a dia foi a régua subir para 500px — ver
                ALTURA_TABELA. Baixar a régua traz a barra de volta. */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {(quadro || []).length === 0
                    ? <div style={{ fontSize: '13px', color: '#999' }}>Sem dados de quadro para o recorte.</div>
                    : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thMini, padding: '0 6px 7px 0' }}>Cargo</th>
                                    <th style={thMiniCentro}>Real</th>
                                    <th style={thMiniCentro}>Ideal</th>
                                    <th style={thMiniCentro}>Dif.</th>
                                    {/* Fantasmas: ativos com 5+ faltas de escala consecutivas em curso
                                        (ainda ausentes). Informativo — NÃO abate do Real. */}
                                    <th style={thMiniCentro} title="Ativos com 5+ faltas de escala consecutivas em curso (ainda ausentes). Não abate do Real.">Fant.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quadro.map((l, i) => (
                                    <tr key={`${l.cargo}-${i}`} style={{ borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                                        <td style={tdCargo}>{l.cargo}</td>
                                        <td style={td}>{fmtNum(l.real)}</td>
                                        <td style={{ ...td, color: '#94a3b8' }}>{fmtNum(l.ideal)}</td>
                                        <td style={td}>{chipDif(l.dif)}</td>
                                        {celFant(l.fantasmas)}
                                    </tr>
                                ))}
                            </tbody>
                            {/* Total: sem faixa de cor (as outras duas tabelas não têm nenhuma);
                                quem o separa das linhas é a borda mais grossa + o negrito. */}
                            <tfoot>
                                <tr style={{ borderTop: `2px solid ${COR.bordaSuave}` }}>
                                    <td style={{ ...tdCargo, fontWeight: 800 }}>Total</td>
                                    <td style={{ ...td, fontWeight: 800 }}>{fmtNum(totReal)}</td>
                                    <td style={{ ...td, fontWeight: 800, color: '#94a3b8' }}>{fmtNum(totIdeal)}</td>
                                    <td style={td}>{chipDif(totReal - totIdeal)}</td>
                                    <td style={{ ...td, fontWeight: 800 }}>{fmtNum(totFant)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
            </div>
        </div>
    );
}

// --- Card de indicador (mesma casca visual dos cards de venda da Operação) ----
// Ícone + título em negrito + número grande + (opcional) chip, variação do mercado
// e rodapé. `chip`/`rodape` são nós JSX/texto já prontos (podem ser null).
// `mercadoEvol` = variação RELATIVA (%) da taxa do mercado vs o mesmo período do
// ano anterior (ver LinhaMercado) — mesma linha dos cards de venda da Operação.
// `bomSubir` diz a direção boa: no absenteísmo, subir é RUIM.
// `dica` = tooltip do card inteiro (detalhe que não cabe no rodapé estreito).
function CardMetrica({ icone, cor, rotulo, valor, chip, mercadoEvol, bomSubir = true, rodape, dica }) {
    return (
        <div title={dica || undefined} style={{ ...cartao, padding: CARD_PAD, display: 'flex', flexDirection: 'column', gap: CARD_GAP, minHeight: CARD_MIN_H }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: cor }}>
                {icone}
                <span style={rotuloCard}>{rotulo}</span>
            </div>
            <div style={{ ...valorCard, color: COR.texto }}>{valor}</div>
            {chip && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>{chip}</div>}
            <LinhaMercado variacao={mercadoEvol} bomSubir={bomSubir} />
            {rodape && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 'auto' }}>{rodape}</div>}
        </div>
    );
}

// Badge neutro da TAXA absoluta (%_Absoluto_Faltas / %_Absoluto_Atestados do BI):
// mostra o % da própria métrica (da loja em foco, ou da rede na visão Rede) ao lado
// do chip de desvio x mercado. Sem seta — é um nível, não uma variação.
function badgeTaxa(dado) {
    if (!dado || dado.loja == null) return null;
    return (
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', backgroundColor: '#f1f5f9', borderRadius: '6px', padding: '2px 7px' }}>
            {fmt1(dado.loja)}%
        </span>
    );
}


// --- Faixa de indicadores em cards (topo da visão RH) ------------------------
// Contratados/Desligados/Turnover vêm de `movimentacao`. Absenteísmo vem de
// `absenteismo` (padrão BI MCDES_16: TAXA = ocorrências ÷ (headcount × dias úteis);
// o mercado é a rede PONDERADA por headcount). Faltas e Atestados mostram a
// CONTAGEM crua (`qtd`) no card; o chip traz a TAXA absoluta (%_Absoluto do BI) +
// o desvio x mercado. Só o Absenteísmo é exibido como % no número grande. Os três
// dependem do espelho de ponto (carga mensal) — sem dado no período, o bloco vem
// todo null e os cards mostram "—".
//
// Julia, 2026-07-21: os cards GC / HORA e VENDAS / HORA saíram desta faixa. As
// duas medidas já aparecem inteiras nos visuais de produtividade lá embaixo
// (ranking por loja com a linha Total da rede + evolução mensal) — repeti-las
// aqui só alongava a faixa de indicadores, que é do RH (movimentação e ponto).
function IndicadoresRH({ mov, absenteismo }) {
    const faltas = absenteismo?.faltas;
    const atestados = absenteismo?.atestados;
    const absTotal = absenteismo?.absenteismo;
    // Sem ponto importado no período → o backend manda o bloco todo null.
    const semPonto = !faltas && !atestados && !absTotal;
    const notaPonto = semPonto ? 'Sem dado de ponto no período (carga mensal).' : null;
    // Faltas/Atestados: contagem crua (loja em foco, ou total da rede na visão Rede).
    const qtd = (m) => (m && m.qtd != null ? fmtNum(m.qtd) : '—');
    // Absenteísmo: taxa % (já calculada — taxa da rede na visão Rede, da loja no foco).
    const pct = (m) => (m && m.loja != null ? `${fmt1(m.loja)}%` : '—');
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '18px' }}>
            <CardMetrica icone={<UserPlus size={18} />} cor="#16a34a" rotulo="CONTRATADOS" valor={fmtNum(mov?.contratados)} />
            <CardMetrica icone={<UserMinus size={18} />} cor="#dc2626" rotulo="DESLIGADOS" valor={fmtNum(mov?.desligados)} />
            <CardMetrica icone={<RefreshCw size={18} />} cor="#7c3aed" rotulo="TURNOVER" valor={mov?.turnover == null ? '—' : `${Math.round(mov.turnover)}%`}
            rodape={`Desligados ÷ Ativos (${fmtNum(mov?.hcMedio == null ? null : Math.round(mov.hcMedio))})`}
            />
            {/* Faltas/Atestados: contagem no número grande, taxa da loja no badge e,
                na linha de baixo, quanto a TAXA do mercado se moveu contra o mesmo
                período do ano anterior. Nos três, a taxa SUBIR é ruim (`bomSubir`
                false): mais falta, mais atestado, mais absenteísmo. */}
            <CardMetrica icone={<CalendarX size={18} />} cor="#d97706" rotulo="FALTAS"
                valor={qtd(faltas)} chip={badgeTaxa(faltas)} mercadoEvol={faltas?.mercadoEvolPct} bomSubir={false} rodape={notaPonto} />
            <CardMetrica icone={<Stethoscope size={18} />} cor="#0891b2" rotulo="ATESTADOS"
                valor={qtd(atestados)} chip={badgeTaxa(atestados)} mercadoEvol={atestados?.mercadoEvolPct} bomSubir={false} rodape={notaPonto} />
            <CardMetrica icone={<Percent size={18} />} cor={COR.vermelhoEscuro} rotulo="% ABSENTEÍSMO"
                valor={pct(absTotal)} mercadoEvol={absTotal?.mercadoEvolPct} bomSubir={false}
                rodape={notaPonto || 'Faltas + atestados ÷ (efetivo × dias úteis).'} />
        </div>
    );
}

// --- Card de Nota de pesquisa (Desligamento / Primeiros 15 dias) --------------
// Nota geral em destaque + chip loja × mercado + nota por seção. As duas pesquisas
// saem em 1-5 (Julia, 2026-07-21 — os 15 dias vinham em índice 0-100 e passaram a
// 1-5 no cálculo, para as duas serem lidas na mesma régua). O NPS (−100..+100)
// segue como métrica à parte, só nos 15 dias. O `escala` decide a formatação;
// `comNps` liga a badge de NPS. O ramo '0-100' fica de pé para uma pesquisa futura
// que nasça em índice.
// Higher é melhor nas duas → no chip, acima do mercado é bom (▲ verde).
// Estados: dado null = tabela ainda indisponível ("em breve"); dado.geral null =
// existe, mas sem resposta pontuável no período ("sem respostas").
// `agregado` = a nota vem de VÁRIAS lojas (visão Rede OU grupo de lojas) → o rodapé
// vira "média de N lojas · M resp." em vez de "N respostas". Para uma loja só é false.
function CardNota({ titulo, icone, dado, escala, comNps, agregado }) {
    // Formata a nota conforme a escala: 1-5 com 1 casa (4,2); 0-100 inteiro (82).
    const fmtNota = (v) => (v == null ? '—' : escala === '1-5' ? fmt1(v) : String(Math.round(v)));
    const unidade = escala === '1-5' ? '/5' : '/100';

    // `extra` = badge de NPS (só nos 15 dias) no canto direito do cabeçalho.
    const header = (extra) => (
        <TituloBloco icone={icone} cor={COR.vermelhoEscuro} texto={titulo} acao={extra} />
    );

    // Tabela nova ainda indisponível no ambiente (guard do backend devolveu null).
    if (!dado) {
        return (
            <div style={{ ...cartao, padding: CARD_PAD, display: 'flex', flexDirection: 'column', gap: '12px', minHeight: CARD_MIN_H }}>
                {header()}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#aaa' }}>
                    Em breve — sem dados da pesquisa ainda.
                </div>
            </div>
        );
    }

    // NPS (só 15 dias): badge com sinal (+40 / −12). Cor pelo sinal (verde/vermelho).
    const npsBadge = comNps && dado.nps != null ? (
        <span style={{ fontSize: '11px', fontWeight: 800, color: dado.nps >= 0 ? '#16a34a' : '#dc2626', backgroundColor: dado.nps >= 0 ? '#dcfce7' : '#fee2e2', borderRadius: '6px', padding: '2px 8px' }}>
            NPS {dado.nps > 0 ? '+' : ''}{dado.nps}
        </span>
    ) : null;

    // Rodapé de contexto: loja única → "N respostas"; agregado (Rede/grupo) →
    // "média de X lojas · N resp.".
    const contexto = agregado
        ? `média de ${fmtNum(dado.lojasComNota)} loja${dado.lojasComNota === 1 ? '' : 's'} · ${fmtNum(dado.n)} resp.`
        : `${fmtNum(dado.n)} resposta${dado.n === 1 ? '' : 's'}`;

    return (
        <div style={{ ...cartao, padding: CARD_PAD, display: 'flex', flexDirection: 'column', gap: CARD_GAP, minHeight: CARD_MIN_H }}>
            {header(npsBadge)}

            {/* Nota geral em destaque + unidade da escala */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ ...valorCard, color: COR.texto }}>{fmtNota(dado.geral)}</span>
                {/* A unidade acompanha o número grande (valorCard) — encolheu junto com ele. */}
                {dado.geral != null && <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8' }}>{unidade}</span>}
            </div>

            {/* Rank da loja + contexto (respostas / lojas) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '11px', color: '#94a3b8' }}>
                {/* Rank só no foco de uma loja (o backend só o preenche nesse caso). */}
                {dado.rank != null && (
                    <span style={{ fontWeight: 800, color: COR.vermelhoEscuro, backgroundColor: COR.amareloFundo, borderRadius: '6px', padding: '2px 8px' }}>
                        {dado.rank}º de {dado.totalRank}
                    </span>
                )}
                <span>{contexto}</span>
            </div>

            {/* Quanto a nota do mercado se moveu contra o mesmo período do ano
                anterior, em % relativo. ⚠️ A escala vai de 1 a 5 (o piso é 1, não 0),
                então a variação sai menor do que a intuição sugere: de 3 para 4 — um
                degrau enorme de percepção — aparece como +33%, não +100%.
                Nota mais alta é melhor nas duas pesquisas. Só existe no foco de uma
                loja: na visão Rede a nota grande JÁ é a do mercado. */}
            <LinhaMercado variacao={dado.mercadoEvolPct} />

            {/* Sem nota geral = existe a tabela, mas ninguém respondeu (parte pontuável) no período */}
            {dado.geral == null ? (
                <div style={{ fontSize: '12px', color: '#aaa', paddingTop: '4px' }}>Sem respostas no período.</div>
            ) : (
                /* As seções fluem em COLUNAS conforme a largura do card (Julia,
                   2026-07-17): numa linha inteira, uma coluna só de 6 seções deixaria o
                   rótulo e a nota nas pontas opostas de um card larguíssimo. Com
                   auto-fit, ~3 colunas na tela cheia e 1 coluna quando o card estreita. */
                <div style={{ borderTop: `1px solid ${COR.bordaSuave}`, paddingTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '5px 22px' }}>
                    {(dado.secoes || []).map((s) => (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '12px' }}>
                            <span style={{ flex: 1, color: '#475569' }}>{s.titulo}</span>
                            <span style={{ fontWeight: 700, color: s.nota == null ? '#cbd5e1' : COR.texto }}>{fmtNota(s.nota)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// --- Blocos: Produtividade ---------------------------------------------------
// Réplica da aba "Produtividade" do BI MCDES_16. Dois cards, cada um com duas abas:
//   - Ranking das lojas (barras): GC/hora ↔ Faturamento/Efetivo
//   - Evolução mensal:            GC/hora ↔ Faturamento/Efetivo
// `dado` null = sem espelho de ponto importado no período.
//
// A barra é comparativa: a largura é relativa à MAIOR do conjunto, não a uma
// escala absoluta — o que interessa é quem está na frente de quem.
const MESES_ABREV = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Moldura comum dos blocos (cabeçalho + corpo ou aviso de "sem dado"). `acao` é o
// canto direito do cabeçalho — as abas do ranking e o toggle do Fat/Efetivo.
function CartaoProdutividade({ titulo, icone, cor, vazio, acao, children }) {
    return (
        // `height: 100%` + linha em `stretch`: o card acompanha o vizinho mais alto da
        // linha (o ranking, que cresce com o nº de lojas) em vez de terminar antes e
        // deixar um buraco embaixo. Quem consome a sobra é o gráfico, via `flex: 1`.
        <div style={{ ...cartao, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', minHeight: 0 }}>
            <TituloBloco icone={icone} cor={cor} texto={titulo} acao={acao} />
            {vazio ? <div style={{ fontSize: '13px', color: '#999' }}>{vazio}</div> : children}
        </div>
    );
}

// Abas do tipo "pílula" (ranking: GC ↔ Vendas; Fat/Efetivo: mês ↔ dia útil).
// `opcoes` = [{ id, rotulo }]. Visual discreto para não competir com o gráfico.
function Abas({ opcoes, valor, onChange }) {
    return (
        <div style={{ display: 'flex', gap: '2px', backgroundColor: '#f1f5f9', borderRadius: '7px', padding: '2px' }}>
            {opcoes.map((o) => {
                const ativa = o.id === valor;
                return (
                    <button
                        key={o.id} type="button" onClick={() => onChange(o.id)}
                        style={{
                            border: 'none', cursor: 'pointer', borderRadius: '5px', padding: '3px 10px',
                            fontFamily: FONTE_DADOS, fontSize: '11px', fontWeight: 700, letterSpacing: '.02em',
                            backgroundColor: ativa ? '#fff' : 'transparent',
                            color: ativa ? COR.vermelhoEscuro : '#94a3b8',
                            boxShadow: ativa ? '0 1px 2px rgba(0,0,0,0.10)' : 'none',
                        }}
                    >
                        {o.rotulo}
                    </button>
                );
            })}
        </div>
    );
}

// "Crew, Estoquista, Auxiliar de Serviços Gerais e Promotor de Vendas" — a lista
// vem do backend (payload), NUNCA escrita à mão aqui: ela é o denominador real do
// cálculo, e uma legenda que diverge do que se calcula é pior que legenda nenhuma.
const listaCargos = (cargos) => {
    const c = cargos || [];
    if (!c.length) return 'efetivo operacional';
    return c.length === 1 ? c[0] : `${c.slice(0, -1).join(', ')} e ${c[c.length - 1]}`;
};

// Selo do mês parcial. O espelho de ponto é um CSV mensal extraído antes de o
// ponto consolidar: as horas por pessoa ficam curtas e a razão infla (jul/2026:
// 2,57 h/pessoa/dia contra 3,6-4,0 de todo mês fechado). O BI tem o mesmo pico e
// não avisa; nós mostramos o mesmo número, mas marcado.
function SeloParcial({ ate }) {
    return (
        <span style={{ fontSize: '10px', fontWeight: 700, color: '#b45309', backgroundColor: '#fef3c7', borderRadius: '5px', padding: '2px 7px', whiteSpace: 'nowrap' }}>
            parcial · espelho até {ate ? `${ate.slice(8, 10)}/${ate.slice(5, 7)}` : '—'}
        </span>
    );
}

// Ranking das lojas (as barras do BI), com abas GC/hora ↔ Faturamento/Efetivo. As
// duas medidas dividem o MESMO denominador (horas do efetivo operacional) — a aba
// troca o numerador (clientes → reais) e a régua (por hora → por funcionário
// equivalente, ÷160), que é como o BI publica cada uma. Só aparece para quem
// enxerga a rede: quem é preso a uma loja não tem com quem se comparar.
// A loja em foco fica destacada para o gestor se achar na lista sem procurar.
function BlocoProdutividadeRanking({ dado, focoLojas }) {
    const [aba, setAba] = React.useState('gc');
    const ehGc = aba === 'gc';
    // O backend manda as duas medidas na mesma linha, ordenadas por GC/hora — a
    // ordem da aba Vendas é OUTRA, então reordenamos aqui (sem ida ao servidor).
    const ranking = React.useMemo(() => {
        const val = (l) => (ehGc ? l.gc : l.vendas);
        return (dado?.ranking || []).filter((l) => val(l) != null).sort((a, b) => val(b) - val(a));
    }, [dado, ehGc]);
    const maior = ranking.length ? (ehGc ? ranking[0].gc : ranking[0].vendas) : 0;
    const fmt = ehGc ? fmtGcH : (n) => fmtNum(Math.round(n));
    // Total da rede (ponderado, calculado no backend) — a régua do rodapé.
    const total = ehGc ? dado?.totalRede?.gc : dado?.totalRede?.vendas;

    return (
        <CartaoProdutividade
            titulo={ehGc ? 'Produtividade — GC por hora' : 'Produtividade — Faturamento / Efetivo'}
            icone={<Gauge size={18} />} cor={COR.vermelhoEscuro}
            acao={<Abas opcoes={[{ id: 'gc', rotulo: 'GC' }, { id: 'vendas', rotulo: 'Fat.' }]} valor={aba} onChange={setAba} />}
            vazio={!dado ? 'Sem dado de ponto no período (carga mensal).' : (!ranking.length ? 'Sem produtividade no recorte.' : null)}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minHeight: 0, maxHeight: '300px', overflowY: 'auto' }}>
                {ranking.map((l) => {
                    const v = ehGc ? l.gc : l.vendas;
                    const destaque = focoLojas.includes(l.loja);
                    return (
                        <div key={l.loja} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '34px', fontSize: '11px', fontWeight: 700, color: destaque ? COR.vermelhoEscuro : '#64748b', flexShrink: 0 }}>{l.loja}</span>
                            {/* Trilho cinza + barra proporcional à maior do conjunto. */}
                            <div style={{ flex: 1, height: '14px', backgroundColor: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ width: maior > 0 ? `${(v / maior) * 100}%` : 0, height: '100%', backgroundColor: destaque ? COR.vermelhoEscuro : COR.amarelo, borderRadius: '3px' }} />
                            </div>
                            <span style={{ width: ehGc ? '38px' : '56px', fontSize: '12px', fontWeight: 700, color: COR.texto, textAlign: 'right', flexShrink: 0 }}>{fmt(v)}</span>
                        </div>
                    );
                })}
            </div>
            {/* Linha "Total (rede)" (Julia, 2026-07-21): a régua contra a qual as barras
                são lidas. É a PONDERADA da rede (Σ GC ÷ Σ horas, vem pronta do backend),
                NÃO a média das barras acima — e por isso não coincide com o meio da
                lista: loja grande pesa mais, como no BI. Fica fora da área que rola,
                colada no rodapé, para continuar visível enquanto o gestor rola a lista. */}
            {total != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderTop: `1px solid ${COR.bordaSuave}`, paddingTop: '8px' }}>
                    <span style={{ flex: 1, fontSize: '12px', fontWeight: 800, color: COR.texto }}>Total (rede)</span>
                    <span style={{ width: ehGc ? '38px' : '56px', fontSize: '13px', fontWeight: 800, color: COR.texto, textAlign: 'right', flexShrink: 0 }}>{fmt(total)}</span>
                </div>
            )}
            <div style={{ fontSize: '11px', color: '#94a3b8', borderTop: `1px solid ${COR.bordaSuave}`, paddingTop: '8px' }}>
                {ehGc
                    ? 'Clientes por hora trabalhada.'
                    : 'Reais (R$) por funcionário equivalente no mês (horas ÷ 160) — a medida Produtividade_Vendas do BI.'}
                {' '}Conta só o efetivo operacional:{' '}
                <strong style={{ color: '#64748b' }}>{listaCargos(dado?.cargosOperacionais)}</strong>.
            </div>
        </CartaoProdutividade>
    );
}

// Gráfico de colunas de uma série mensal. Compartilhado pelas duas evoluções
// (GC/hora e Faturamento/Efetivo) — a única diferença entre elas é a régua e o
// formato do número, que entram por parâmetro.
// Mês `parcial` sai LISTRADO e com o valor em âmbar: o número é o mesmo do BI,
// mas o leitor precisa saber que ele está inflado por espelho incompleto.
function ColunasMes({ serie, fmt, cor }) {
    const maior = serie.reduce((m, x) => Math.max(m, x.valor), 0);
    return (
        // `flex: 1` sobre um piso de 190px: o gráfico cresce para preencher a altura que
        // o card ganhou ao acompanhar o ranking, em vez de deixar branco embaixo.
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '6px', flex: 1, minHeight: '190px' }}>
            {serie.map((x) => {
                const mes = Number(x.mes.slice(5, 7));
                // Listras diagonais = "não confie neste número como nos outros".
                const fundo = x.parcial
                    ? `repeating-linear-gradient(45deg, ${COR.amarelo}, ${COR.amarelo} 4px, #fde68a 4px, #fde68a 8px)`
                    : cor;
                return (
                    <div key={x.mes} title={x.parcial ? `Espelho de ponto incompleto (até ${x.ate}) — valor inflado.` : undefined}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: x.parcial ? '#b45309' : COR.texto, flexShrink: 0 }}>{fmt(x.valor)}</span>
                        {/* TRILHO da barra — a base do percentual (Julia, 2026-07-17).
                            A barra precisa medir 100% de um espaço que NÃO contenha os dois
                            rótulos. Quando ela media a coluna inteira, a barra do mês mais
                            alto pedia a altura toda, não cabia junto do valor e do mês, e o
                            flex a encolhia; as barras baixas cabiam e não encolhiam — ou
                            seja, o topo da escala era comprimido e a diferença entre os
                            meses aparecia menor do que é. Isso saltava no Faturamento /
                            Efetivo, que fica sozinho na linha e no piso de 190px (os ~32px
                            dos rótulos pesam muito mais aí do que num card esticado). */}
                        {/* A barra ocupa 58% da coluna, centralizada (Julia, 2026-07-21):
                            a COLUNA continua dividindo a largura igualmente (é ela que
                            alinha o valor em cima e o mês embaixo), mas a barra em si
                            fica mais fina — colada uma na outra elas viravam um bloco
                            só, e o desenho mensal se lia pior. */}
                        <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                            <div style={{ width: '58%', height: maior > 0 ? `${(x.valor / maior) * 100}%` : 0, background: fundo, borderRadius: '3px 3px 0 0', minHeight: '2px' }} />
                        </div>
                        <span style={{ fontSize: '10px', color: x.parcial ? '#b45309' : '#94a3b8', fontWeight: x.parcial ? 700 : 400, flexShrink: 0 }}>{MESES_ABREV[mes - 1]}</span>
                    </div>
                );
            })}
        </div>
    );
}

// Rodapé comum das evoluções: de quem é a série + selo do mês parcial, se houver.
function RodapeEvolucao({ serie, focoLojas, extra }) {
    const parcial = serie.find((x) => x.parcial);
    const deQuem = !focoLojas.length ? 'Série da rede'
        : focoLojas.length === 1 ? `Série da loja ${focoLojas[0]}`
            : `Série de ${focoLojas.length} lojas`;
    return (
        <div style={{ fontSize: '11px', color: '#94a3b8', borderTop: `1px solid ${COR.bordaSuave}`, paddingTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: '180px' }}>
                {deQuem} · o ano todo (não segue o filtro de mês).{extra ? ` ${extra}` : ''}
            </span>
            {parcial && <SeloParcial ate={parcial.ate} />}
        </div>
    );
}

// Evolução mensal — GC/hora e Faturamento/Efetivo no MESMO card, em abas
// (Julia, 2026-07-21). Antes eram dois cards lado a lado; como as duas séries são
// mensais, do mesmo ano e do mesmo recorte, ler uma DEPOIS da outra no mesmo lugar
// compara melhor do que ler as duas de relance em cards separados — e devolve
// metade da largura da tela ao dashboard.
//
// Ignora o filtro de mês de propósito (é série, não recorte — o BI faz igual), mas
// respeita a loja em foco.
//
// O toggle "Mês ↔ Dia útil" do Faturamento/Efetivo SAIU: era só uma troca de
// unidade (÷22 dias úteis) que ninguém usava, e a régua oficial é a mensal
// (R$ por funcionário equivalente / mês) — a mesma do BI.
function BlocoEvolucao({ dado, focoLojas, ano }) {
    const [aba, setAba] = React.useState('gc');
    const ehGc = aba === 'gc';
    const serie = (ehGc ? dado?.evolucao : dado?.fatEfetivo) || [];

    return (
        <CartaoProdutividade
            titulo={`Evolução ${ehGc ? 'GC / hora' : 'Faturamento / Efetivo'} — ${ano || ''}`}
            icone={<TrendingUp size={18} />} cor={ehGc ? COR.amarelo : '#0d9488'}
            acao={<Abas opcoes={[{ id: 'gc', rotulo: 'GC / hora' }, { id: 'fat', rotulo: 'Fat. / Efetivo' }]} valor={aba} onChange={setAba} />}
            vazio={!dado ? 'Sem dado de ponto no período (carga mensal).' : (!serie.length ? 'Sem série no ano.' : null)}
        >
            <ColunasMes
                serie={serie}
                fmt={ehGc ? fmtGcH : (n) => fmtNum(Math.round(n))}
                cor={ehGc ? COR.vermelhoEscuro : '#0d9488'}
            />

            {/* Informativo do cálculo (pedido da Julia): o gestor precisa saber o que
                está lendo — "R$ 27 mil" sem régua não significa nada. Só na aba do
                Faturamento/Efetivo: o GC/hora se explica no rodapé, em uma linha. */}
            {!ehGc && (
                <div style={{ fontSize: '11px', color: '#64748b', backgroundColor: '#f8fafc', border: `1px solid ${COR.bordaSuave}`, borderRadius: '7px', padding: '9px 11px', lineHeight: 1.55 }}>
                    <strong style={{ color: COR.texto }}>Como é calculado:</strong> as horas trabalhadas (normal + extras) viram
                    “funcionários equivalentes” dividindo por <strong>160h</strong> (jornada mensal média de 1 funcionário).
                    O faturamento é dividido por esse efetivo:
                    <div style={{ marginTop: '5px', color: COR.texto, fontWeight: 700 }}>
                        R$ ÷ (horas ÷ 160) = <span style={{ color: '#0d9488' }}>R$ por funcionário / mês</span>
                    </div>
                    <div style={{ marginTop: '5px' }}>
                        As horas são as do <strong>efetivo operacional</strong> ({listaCargos(dado?.cargosOperacionais)}) —
                        gerência e administrativo ficam de fora. É o mesmo denominador do GC/hora, e o mesmo recorte
                        que o BI aplica na página Produtividade.
                    </div>
                </div>
            )}

            {/* A lista de cargos se repete no rodapé do GC/hora de propósito: quem é
                preso a uma loja NÃO recebe o ranking, então esta é a única vez em que
                ele leria quem entra na conta. */}
            <RodapeEvolucao
                serie={serie} focoLojas={focoLojas}
                extra={ehGc ? `Conta só o efetivo operacional: ${listaCargos(dado?.cargosOperacionais)}. Mês sem ponto importado não aparece.` : null}
            />
        </CartaoProdutividade>
    );
}

// Cabeçalho de coluna das mini-tabelas (Experiência/Aniversariantes). Fica fixo
// (sticky) no topo enquanto a lista rola, com fundo branco para as linhas não
// aparecerem por trás.
const thMini = { fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', padding: '0 0 7px', textAlign: 'left', whiteSpace: 'nowrap', backgroundColor: '#fff', position: 'sticky', top: 0 };
const thMiniDir = { ...thMini, textAlign: 'right' };
// Variante centralizada: só o Real × Ideal usa, porque lá as colunas numéricas são
// curtas (1 a 3 dígitos) e ficam centralizadas (Julia, 2026-07-16).
const thMiniCentro = { ...thMini, textAlign: 'center' };
// Célula de nome + cargo (subtítulo cinza). Comum às duas tabelas.
function CelulaColaborador({ nome, cargo, sufixo }) {
    return (
        <td style={{ padding: '7px 0', verticalAlign: 'top' }}>
            <div style={{ fontSize: '13px', color: COR.texto, fontWeight: 600 }}>{nome}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{cargo || '—'}{sufixo || ''}</div>
        </td>
    );
}

// Cabeçalho clicável: 1º clique ordena crescente (menor → maior), o 2º inverte.
// O botão herda a tipografia do <th> para o rótulo não mudar de aparência ao virar
// clicável — só ganha o ícone de ordenação ao lado.
const botaoOrdenar = {
    background: 'none', border: 'none', padding: 0, margin: 0, width: '100%', cursor: 'pointer',
    font: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
    display: 'flex', alignItems: 'center', gap: '4px',
};
function ThOrdenavel({ rotulo, coluna, ordem, setOrdem, alinhaDireita }) {
    const ativa = ordem.coluna === coluna;
    const Icone = !ativa ? ChevronsUpDown : ordem.asc ? ArrowUp : ArrowDown;
    return (
        <th style={alinhaDireita ? thMiniDir : thMini}>
            <button
                type="button"
                onClick={() => setOrdem((o) => ({ coluna, asc: o.coluna === coluna ? !o.asc : true }))}
                style={{ ...botaoOrdenar, justifyContent: alinhaDireita ? 'flex-end' : 'flex-start' }}
            >
                <span>{rotulo}</span>
                <Icone size={11} style={{ flexShrink: 0, opacity: ativa ? 1 : 0.45 }} />
            </button>
        </th>
    );
}

// Fim de experiência que já passou deixou de ser um prazo a acompanhar: a célula
// vira travessão, igual a quem nunca teve a data preenchida. A pessoa continua na
// lista enquanto a outra experiência estiver correndo.
const expVigente = (iso, hoje) => {
    const d = iso ? String(iso).slice(0, 10) : null;
    return d && d >= hoje ? d : null;
};

// --- Bloco: Experiência ------------------------------------------------------
// Tabela: Colaborador (nome + cargo) | 1ª Exp. | 2ª Exp. (datas de fim das duas
// experiências, vindas da dtbConfere). As três colunas ordenam pelo cabeçalho.
function BlocoExperiencia({ experiencias }) {
    const tdData = { fontSize: '12px', color: '#64748b', textAlign: 'right', whiteSpace: 'nowrap', padding: '7px 0 7px 10px', verticalAlign: 'top' };
    const [ordem, setOrdem] = React.useState({ coluna: 'nome', asc: true });

    const linhas = React.useMemo(() => {
        const hoje = hojeISO();
        const base = (experiencias || []).map((e) => ({
            ...e,
            exp1: expVigente(e.exp1, hoje),
            exp2: expVigente(e.exp2, hoje),
        }));
        const dir = ordem.asc ? 1 : -1;
        return base.sort((a, b) => {
            if (ordem.coluna === 'nome') return dir * String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');
            const x = a[ordem.coluna];
            const y = b[ordem.coluna];
            // Sem data (vencida ou em branco) cai sempre no fim, nos dois sentidos:
            // não há prazo a comparar, e quem tem prazo é que interessa no topo.
            if (!x && !y) return 0;
            if (!x) return 1;
            if (!y) return -1;
            return dir * x.localeCompare(y); // ISO 'YYYY-MM-DD' ordena como texto
        });
    }, [experiencias, ordem]);

    return (
        <div style={{ ...cartao, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%', minHeight: 0 }}>
            <TituloBloco icone={<CalendarClock size={18} />} cor={COR.vermelhoEscuro} texto="Em período de experiência" />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {(experiencias || []).length === 0
                    ? <div style={{ fontSize: '13px', color: '#999' }}>Ninguém em experiência no recorte.</div>
                    : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <ThOrdenavel rotulo="Colaborador" coluna="nome" ordem={ordem} setOrdem={setOrdem} />
                                    <ThOrdenavel rotulo="1ª Exp." coluna="exp1" ordem={ordem} setOrdem={setOrdem} alinhaDireita />
                                    <ThOrdenavel rotulo="2ª Exp." coluna="exp2" ordem={ordem} setOrdem={setOrdem} alinhaDireita />
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((e, i) => (
                                    <tr key={i} style={{ borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                                        <CelulaColaborador nome={e.nome} cargo={e.cargo} />
                                        <td style={tdData}>{fmtData(e.exp1)}</td>
                                        <td style={tdData}>{fmtData(e.exp2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>
        </div>
    );
}

// --- Bloco: Aniversariantes --------------------------------------------------
// Tabela: Colaborador (nome + cargo, e a loja no sufixo na visão Rede) | Dia.
function BlocoAniversariantes({ aniversariantes, mostrarLoja }) {
    return (
        <div style={{ ...cartao, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px', height: '100%', minHeight: 0 }}>
            <TituloBloco icone={<Cake size={18} />} cor={COR.amarelo} texto="Aniversariantes do mês" />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {(aniversariantes || []).length === 0
                    ? <div style={{ fontSize: '13px', color: '#999' }}>Sem aniversariantes no recorte.</div>
                    : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thMini}>Colaborador</th>
                                    <th style={thMiniDir}>Dia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(aniversariantes || []).map((a, i) => (
                                    <tr key={i} style={{ borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                                        <CelulaColaborador nome={a.nome} cargo={a.cargo} sufixo={mostrarLoja ? ` · ${a.loja}` : ''} />
                                        <td style={{ fontSize: '13px', fontWeight: 700, color: COR.texto, textAlign: 'right', whiteSpace: 'nowrap', padding: '7px 0 7px 10px', verticalAlign: 'top' }}>{String(a.dia).padStart(2, '0')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>
        </div>
    );
}

// =====================================================================
// `carregando` não desenha mais nada aqui: quem avisa o usuário é o
// OverlayCarregando do DashboardOperacao, que cobre as duas visões (Operação e RH)
// com o mesmo véu. Antes esta tela tinha o próprio texto "Carregando painel de
// RH…", que agora sairia esmaecido POR BAIXO do véu — duas mensagens para o mesmo
// estado. Na primeira carga o retorno é vazio e o véu ocupa a área sozinho.
export default function DashboardRH({ dados }) {
    if (!dados) return null;
    const focoLojas = dados.focoLojas || [];   // [] = Rede; 1 = loja; 2+ = grupo
    // Mostra a loja de cada pessoa quando o recorte NÃO é uma loja única (Rede ou
    // grupo) — aí a coluna some, porque todos seriam da mesma loja.
    const mostrarLoja = focoLojas.length !== 1;
    // Agregado = a nota das pesquisas vem de várias lojas (Rede ou grupo).
    const agregado = focoLojas.length !== 1;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            {/* Topo: faixa de indicadores em cards (estilo dos cards da Operação) */}
            <IndicadoresRH mov={dados.movimentacao} absenteismo={dados.absenteismo} />

            {/* Meio: as três tabelas na MESMA linha (Julia, 2026-07-17). Antes o
                Real×Ideal ficava sozinho à esquerda e as outras duas empilhadas à
                direita — como as alturas nunca batiam, sobrava uma faixa branca embaixo
                da coluna mais curta. Agora as três dividem a linha, com a mesma régua de
                altura (ALTURA_TABELA) e rolagem por dentro de cada uma.
                `auto-fit` em vez de 3 colunas fixas: em tela estreita elas quebram para
                2 (e depois 1) em vez de estourar a largura da página. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', alignItems: 'stretch', gridAutoRows: ALTURA_TABELA }}>
                <BlocoRealIdeal quadro={dados.quadroCargos} />
                <BlocoExperiencia experiencias={dados.experiencias} />
                <BlocoAniversariantes aniversariantes={dados.aniversariantes} mostrarLoja={mostrarLoja} />
            </div>

            {/* Faixa inferior: Notas das pesquisas (Desligamento · Primeiros 15 dias),
                lado a lado ocupando a linha inteira. O `maxWidth: 760px` que existia aqui
                prendia os dois cards na metade esquerda e deixava a metade direita da
                tela vazia (Julia, 2026-07-17) — quem usa a largura agora é a lista de
                seções, que se espalha em colunas dentro do card.
                `agregado` (Rede ou grupo) troca o rodapé para "média de N lojas". */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                <CardNota
                    titulo="Nota — Pesquisa de Desligamento"
                    icone={<DoorOpen size={18} />}
                    dado={dados.desligamento}
                    escala="1-5"
                    agregado={agregado}
                />
                <CardNota
                    titulo="Nota — Primeiros 15 dias"
                    icone={<CalendarCheck size={18} />}
                    dado={dados.primeiro15}
                    escala="1-5"
                    comNps
                    agregado={agregado}
                />
            </div>

            {/* Fim da tela: os visuais da aba "Produtividade" do BI MCDES_16 (pedido
                da Julia, 2026-07-17 — antes ficavam no meio, entre os cards e o
                Real × Ideal). Desde 2026-07-21 são DOIS: o ranking por loja e o card
                de evolução com as duas séries em abas (GC/hora · Fat./Efetivo).

                O RANKING só existe para quem enxerga a rede: quem é preso a uma loja
                não tem com quem se comparar. Nesse caso a evolução ocupa a linha
                inteira; com ranking, os dois dividem a linha. */}
            {dados.escritorio ? (
                /* `stretch` (e não `start`): o ranking e a evolução terminam na mesma
                    altura — antes o ranking, mais alto, deixava um buraco embaixo da
                    evolução (Julia, 2026-07-17). */
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(340px, 1.3fr)', gap: '20px', alignItems: 'stretch' }}>
                    <BlocoProdutividadeRanking dado={dados.produtividade} focoLojas={focoLojas} />
                    <BlocoEvolucao dado={dados.produtividade} focoLojas={focoLojas} ano={dados.periodo?.ano} />
                </div>
            ) : (
                <BlocoEvolucao dado={dados.produtividade} focoLojas={focoLojas} ano={dados.periodo?.ano} />
            )}
        </div>
    );
}
